import { useEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, Target } from 'lucide-react';
import { Flag } from '../../components/Flag';
import { PageHeader } from '../../components/common/PageHeader';
import { MONTH_NAMES as MONTHS } from '../../config/months';
import { computeCountryInsights, labelById } from '../../lib/insights';

const fmtMoney = (v) => {
  const n = v || 0, s = n < 0 ? '-' : '', a = Math.abs(n);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${Math.round(a)}`;
};

export const HomePage = ({ user, setActiveModule, forecastData, sellOutData, insightsData }) => {
  // === FORECAST KPIs (region-wide) ===
  const kpi = useMemo(() => {
    if (!forecastData || typeof forecastData !== 'object') return null;
    let totalSales = 0, totalAop = 0, totalGP = 0;
    const monthlySales = Array(12).fill(0);
    const monthlyAop = Array(12).fill(0);
    const actualCount = Array(12).fill(0); // months a country marks as 'actual' (firm)
    Object.values(forecastData).forEach(country => {
      if (!country || typeof country !== 'object') return;
      const salesArr = Array.isArray(country.actualSales) ? country.actualSales : Object.values(country.actualSales || {});
      const aopArr = Array.isArray(country.aopMonthly) ? country.aopMonthly : Object.values(country.aopMonthly || {});
      const cogsArr = Array.isArray(country.cogsMonthly) ? country.cogsMonthly : Object.values(country.cogsMonthly || {});
      const smmArr = Array.isArray(country.smmMonthly) ? country.smmMonthly : Object.values(country.smmMonthly || {});
      salesArr.forEach((v, i) => { if (typeof v === 'number') { totalSales += v; if (i < 12) monthlySales[i] += v; } });
      aopArr.forEach((v, i) => { if (typeof v === 'number') { totalAop += v; if (i < 12) monthlyAop[i] += v; } });
      salesArr.forEach((v, i) => { totalGP += (v || 0) - (cogsArr[i] || 0) - (smmArr[i] || 0); });
      if (Array.isArray(country.monthStatus)) country.monthStatus.forEach((s, i) => { if (i < 12 && s === 'actual') actualCount[i]++; });
    });
    if (totalAop === 0 && totalSales === 0) return null;
    // Which months are firm (actual) vs forecast. Fallback when no monthStatus: a
    // month with sales is treated as firm.
    let monthFirm = actualCount.map(c => c > 0);
    if (!monthFirm.some(Boolean)) monthFirm = monthlySales.map(v => v !== 0);
    return {
      sales: totalSales, aop: totalAop, gp: totalGP,
      achievement: totalAop > 0 ? (totalSales / totalAop) * 100 : null,
      margin: totalSales > 0 ? totalGP / totalSales : 0,
      monthlySales, monthlyAop, monthFirm,
    };
  }, [forecastData]);

  const [chartMode, setChartMode] = useState('cumulative'); // 'cumulative' | 'monthly'
  const [showAllInsights, setShowAllInsights] = useState(false);
  // Size the chart to the COLLAPSED Insights panel so their bases align. Only
  // measure a real (populated) collapsed panel, and the chart height is clamped
  // to a floor so it can never collapse to nothing.
  const insightsRef = useRef(null);
  const [collapsedH, setCollapsedH] = useState(null);
  useEffect(() => {
    const el = insightsRef.current;
    if (!el) return;
    const measure = () => { if (!showAllInsights) { const h = el.offsetHeight; if (h > 400) setCollapsedH(h); } };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-measure once data lands so the initial size matches the Show-less state.
  }, [showAllInsights, forecastData, sellOutData, insightsData]);
  // Chart CARD height = collapsed Insights height (default until measured), so
  // their bases line up exactly; the chart fills the card via flex.
  const chartCardH = collapsedH ?? 530;

  // The path-to-year-end series: actual (firm, solid) vs forecast (dashed) vs AOP,
  // in both cumulative and monthly shapes, with the firm/forecast boundary + totals.
  const path = useMemo(() => {
    if (!kpi) return null;
    const { monthlySales, monthlyAop, monthFirm } = kpi;
    let lastFirm = -1, lastData = -1;
    for (let i = 0; i < 12; i++) { if (monthFirm[i]) lastFirm = i; if (monthlySales[i] || monthlyAop[i]) lastData = i; }
    const end = Math.max(lastData, lastFirm, 0);
    const firstFirm = monthFirm.findIndex(Boolean);

    let cs = 0, ca = 0;
    const cumulative = [], monthly = [];
    for (let i = 0; i <= end; i++) {
      cs += monthlySales[i] || 0; ca += monthlyAop[i] || 0;
      const firm = i <= lastFirm;
      const sVal = monthlySales[i] || 0;
      cumulative.push({
        month: MONTHS[i],
        actual: firm ? cs : null,
        forecast: i >= lastFirm ? cs : null, // starts at the boundary so the dashed line connects
        aop: ca,
      });
      monthly.push({
        month: MONTHS[i],
        actual: firm ? sVal : null,
        forecast: i >= lastFirm ? sVal : null,
        aop: monthlyAop[i] || 0,
      });
    }
    let actualTotal = 0, forecastTotal = 0;
    for (let i = 0; i <= end; i++) { if (i <= lastFirm) actualTotal += monthlySales[i] || 0; else forecastTotal += monthlySales[i] || 0; }
    const boundary = lastFirm >= 0 ? MONTHS[lastFirm] : null;
    const firmLabel = lastFirm >= 0 ? `${MONTHS[Math.max(firstFirm, 0)]}–${MONTHS[lastFirm]}` : '';
    const fcLabel = lastFirm < end ? `${MONTHS[lastFirm + 1]}–${MONTHS[end]}` : '';
    // Split the progress bar by MONTH position (like the chart's x-axis), not by
    // dollars, so the firm/forecast boundary lands on the same month as the line.
    const firmFrac = end >= 0 ? (lastFirm + 1) / (end + 1) : 0;
    return { cumulative, monthly, boundary, endMonth: MONTHS[end], actualTotal, forecastTotal, firmLabel, fcLabel, firmFrac, hasForecast: lastFirm < end };
  }, [kpi]);

  // === INSIGHTS STATUS BOARD — auto conclusions merged with manual overrides ===
  // Auto engine lives in lib/insights (shared with the Admin editor). Manual text
  // and label from the Admin editor (module_data 'insights') take precedence.
  const board = useMemo(() => {
    const auto = computeCountryInsights(forecastData, sellOutData);
    const byKey = Object.fromEntries(auto.map(c => [c.key, c]));
    const manual = insightsData?.countries || {};
    const order = insightsData?.order || [];
    // Saved order first, then any country not in the saved order.
    const keys = [...order.filter(k => byKey[k]), ...auto.map(c => c.key).filter(k => !order.includes(k))];
    return keys.map(k => {
      const c = byKey[k], m = manual[k] || {};
      if (m.hidden) return null; // hidden from the Overview
      const custom = !!(m.text && m.text.trim());
      return {
        ...c,
        labelId: m.label || c.autoLabel,
        label: labelById(m.label || c.autoLabel),
        text: custom ? m.text.trim() : `${c.autoHeadline} — ${c.autoDetail}`,
        custom,
      };
    }).filter(Boolean);
  }, [forecastData, sellOutData, insightsData]);

  const hasForecast = !!kpi;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const firstName = (user?.name || '').split(' ')[0];
  // Full-year (ACT + FC vs AOP) achievement drives the hero — colour with meaning.
  const ach = kpi?.achievement;
  const gap = kpi ? kpi.sales - kpi.aop : 0;
  const heroGrad = ach == null ? 'from-slate-600 to-slate-700'
    : ach >= 100 ? 'from-emerald-500 to-teal-600'
    : ach >= 95 ? 'from-blue-600 to-indigo-600'
    : ach >= 85 ? 'from-amber-500 to-orange-600'
    : 'from-rose-500 to-red-600';
  const periodLabel = `FY ${new Date().getFullYear()} · USD`;

  if (!hasForecast) {
    return (
      <div className="space-y-8">
        <PageHeader eyebrow="Monthly Business Review" title={firstName ? `Welcome back, ${firstName}` : 'LATAM at a glance'} subtitle={today} />
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-700">No regional data yet</p>
            <p className="text-sm text-slate-500 mt-1">Upload Sales &amp; Forecast data to see the executive summary here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero header — tinted gradient with an accent bar for a bit of life */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-blue-50 via-white to-emerald-50/50 px-6 py-5">
        <div className="absolute left-0 inset-y-0 w-1.5 bg-gradient-to-b from-blue-500 to-emerald-400" />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600 mb-1.5">The Monthly Brief</p>
            <h1 className="text-2xl sm:text-[30px] leading-tight font-bold text-slate-900 tracking-tight">The story behind the numbers.</h1>
            {firstName && <p className="text-sm text-slate-500 mt-1">Welcome back, {firstName} — here's where LATAM stands.</p>}
          </div>
          <div className="text-sm font-medium text-slate-500 pt-1">{periodLabel}</div>
        </div>
      </div>

      {/* Full-year plan achievement hero (ACT + FC vs AOP) */}
      {kpi && (
        <div className="grid grid-cols-1 lg:grid-cols-5 rounded-2xl overflow-hidden border border-slate-200/80 shadow-sm">
          {/* Left — gradient achievement */}
          <div className={`lg:col-span-2 bg-gradient-to-br ${heroGrad} p-6 text-white flex flex-col justify-center`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/80">Full-year plan achievement</p>
            <div className="mt-2 flex items-end gap-0.5">
              <span className="text-6xl font-bold leading-none tabular-nums">{ach == null ? '—' : ach.toFixed(1)}</span>
              {ach != null && <span className="text-2xl font-bold mb-1.5">%</span>}
            </div>
            <div className="mt-4">
              <span className="inline-block bg-white/15 rounded-full px-3 py-1 text-xs font-semibold">
                {gap >= 0 ? '+' : '−'}{fmtMoney(Math.abs(gap))} {gap >= 0 ? 'above' : 'below'} full-year plan
              </span>
            </div>
            <p className="mt-4 text-xs text-white/70">Actual + forecast vs AOP · LATAM</p>
          </div>
          {/* Right — the numbers */}
          <div className="lg:col-span-3 bg-white p-6 flex flex-col justify-center divide-y divide-slate-100">
            <div className="flex items-center justify-between py-3.5">
              <span className="text-sm text-slate-500">Sales (ACT + FC)</span>
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{fmtMoney(kpi.sales)}</span>
            </div>
            <div className="flex items-center justify-between py-3.5">
              <span className="text-sm text-slate-500">Full-year plan (AOP)</span>
              <span className="text-2xl font-bold text-slate-900 tabular-nums">{fmtMoney(kpi.aop)}</span>
            </div>
            <div className="flex items-center justify-between py-3.5">
              <span className="text-sm text-slate-500">Gross profit</span>
              <span className="text-right leading-tight">
                <span className="block text-2xl font-bold text-emerald-600 tabular-nums">{fmtMoney(kpi.gp)}</span>
                <span className="text-xs text-slate-400">{(kpi.margin * 100).toFixed(1)}% margin</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Sales against plan + Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* The path to year-end — cumulative actual (firm) vs forecast vs AOP */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 flex flex-col" style={{ height: chartCardH }}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h3 className="text-base font-bold text-slate-800">The path to year-end</h3>
              <p className="text-xs text-slate-500">Actual · forecast · plan (AOP) — USD</p>
            </div>
            <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-semibold shrink-0">
              {['cumulative', 'monthly'].map(m => (
                <button key={m} onClick={() => setChartMode(m)} className={`px-3 py-1 rounded-md capitalize transition-colors ${chartMode === m ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{m}</button>
              ))}
            </div>
          </div>
          {!path || path.cumulative.length === 0 ? (
            <div className="h-[400px] flex items-center justify-center text-sm text-slate-400">No monthly data</div>
          ) : (() => {
            const data = chartMode === 'cumulative' ? path.cumulative : path.monthly;
            return (
              <>
                <div className="flex-1 min-h-0">
                  {/* Remount only when the card height changes (initial measure) so
                      Recharts fills the new size — not on every View more toggle. */}
                  <ResponsiveContainer key={chartCardH} width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 12, right: 18, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                      {path.hasForecast && path.boundary && (
                        <ReferenceArea x1={path.boundary} x2={path.endMonth} fill="#eff6ff" fillOpacity={0.7} ifOverflow="extendDomain" label={{ value: 'Forecast →', position: 'insideTopLeft', fontSize: 10, fill: '#64748b' }} />
                      )}
                      {path.hasForecast && path.boundary && <ReferenceLine x={path.boundary} stroke="#cbd5e1" strokeDasharray="3 3" />}
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} />
                      <Tooltip formatter={(v, n) => [fmtMoney(v), n]} />
                      <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="aop" name="AOP" stroke="#94a3b8" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey="actual" name="Actual" stroke="#2563eb" strokeWidth={3} dot={false} connectNulls />
                      <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#2563eb" strokeWidth={2.5} strokeDasharray="5 4" dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {/* Firm vs forecast progress bar */}
                {path.hasForecast && (path.actualTotal + path.forecastTotal) > 0 && (
                  <div className="mt-3">
                    <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
                      <div className="bg-blue-600" style={{ width: `${path.firmFrac * 100}%` }} />
                      <div className="flex-1" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #bfdbfe 0, #bfdbfe 4px, #eff6ff 4px, #eff6ff 8px)' }} />
                    </div>
                    <div className="flex justify-between mt-1.5 text-[11px]">
                      <span className="text-slate-600"><span className="font-semibold text-slate-800">{path.firmLabel} actual</span> {fmtMoney(path.actualTotal)}</span>
                      <span className="text-slate-500">{path.fcLabel} forecast <span className="font-semibold text-slate-700">{fmtMoney(path.forecastTotal)}</span></span>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Insights — per-country status board (auto + your notes/labels) */}
        <div ref={insightsRef} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 flex flex-col">
          <div className="mb-4">
            <h3 className="text-base font-bold text-slate-800">Insights</h3>
            <p className="text-xs text-slate-500">Where each market stands this month</p>
          </div>
          {board.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
              <Target className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-600">No country data yet</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {(showAllInsights ? board : board.slice(0, 4)).map(c => (
                <button key={c.key} onClick={() => { localStorage.setItem('luye_forecast_country', c.key); setActiveModule('forecast'); }}
                  className="w-full text-left rounded-xl border border-slate-200 p-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors group">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`w-2 h-2 rounded-full ${c.label.dot}`} />
                    <Flag country={c.key} size={16} />
                    <span className="text-sm font-bold text-slate-800">{c.name}</span>
                    <span className={`ml-auto text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${c.label.pill}`}>{c.label.name}</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-snug">{c.text}</p>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 mt-1.5 group-hover:gap-1.5 transition-all">View details <ArrowRight className="w-3 h-3" /></span>
                </button>
              ))}
              {board.length > 4 && (
                <button onClick={() => setShowAllInsights(v => !v)} className="w-full text-center text-xs font-semibold text-blue-600 hover:text-blue-700 py-2">
                  {showAllInsights ? 'Show less' : `View more (${board.length - 4})`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};
